import {getLocationMetaActivity, type LocationContext} from "@ots-frontend/ots-data";
import type {LiveMapDisplayOptions} from "./live-map-display";

/** At most this many lines, so a busy yard does not turn into a starburst. */
export const SITE_CONNECTION_LIMIT = 8;
/** Road routes are fetched for only the closest few; each one is a routing call. */
export const SITE_ROAD_ROUTE_LIMIT = 3;

export type SitePoint = {id: string; lat: number; lng: number};

/** A unit a clicked site draws a line to. */
export type SiteConnection = {
  ctx: LocationContext;
  lat: number;
  lng: number;
  /** Straight-line miles from the site. */
  miles: number;
  /** When the unit's possible destinations expect it at this site. */
  etaDttm?: number;
  inShift: boolean;
};

const EARTH_RADIUS_MILES = 3958.8;
const radians = (degrees: number) => degrees * Math.PI / 180;

/** Great-circle miles between two points. */
export const milesBetween = (a: {lat: number; lng: number}, b: {lat: number; lng: number}) => {
  const dLat = radians(b.lat - a.lat);
  const dLng = radians(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(radians(a.lat)) * Math.cos(radians(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_MILES * Math.asin(Math.min(1, Math.sqrt(h)));
};

/** The earliest ETA among the unit's possible destinations that point at this site, or undefined when none do. */
const headedTo = (ctx: LocationContext, siteId: string) => {
  const matches = Object.values(ctx.meta?.possibleDestinations ?? {}).filter(destination => destination.siteId === siteId);
  if (matches.length === 0) return undefined;
  const etas = matches.map(destination => destination.etaDttm).filter((eta): eta is number => eta != null);
  return {etaDttm: etas.length ? Math.min(...etas) : undefined};
};

/**
 * The units a clicked site connects to under the display options: within the radius and closest first, or those
 * whose possible destinations include the site, soonest first. Units without a position are skipped.
 */
export const findSiteConnections = (
  site: SitePoint,
  locations: LocationContext[],
  display: Pick<LiveMapDisplayOptions, "siteLinks" | "siteLinkRadiusMiles">,
): SiteConnection[] => {
  if (display.siteLinks === "off") return [];
  const connections: SiteConnection[] = [];
  for (const ctx of locations) {
    if (!ctx.location) continue;
    const point = {lat: ctx.location.latitude, lng: ctx.location.longitude};
    const miles = milesBetween(site, point);
    const headed = headedTo(ctx, site.id);
    if (display.siteLinks === "headed" ? !headed : miles > display.siteLinkRadiusMiles) continue;
    connections.push({ctx, ...point, miles, etaDttm: headed?.etaDttm, inShift: getLocationMetaActivity(ctx.meta)?.inShift ?? false});
  }
  const order = display.siteLinks === "headed"
    ? (a: SiteConnection, b: SiteConnection) => (a.etaDttm ?? Infinity) - (b.etaDttm ?? Infinity) || a.miles - b.miles
    : (a: SiteConnection, b: SiteConnection) => a.miles - b.miles;
  return connections.sort(order).slice(0, SITE_CONNECTION_LIMIT);
};

/** Matches a routing result back to its unit by the coordinates it was asked for. */
export const routeKey = (lat: number, lng: number) => `${lat.toFixed(6)},${lng.toFixed(6)}`;

export type SiteRoadRoute = {
  /** Driving distance, meters. */
  meters: number;
  /** Driving time, milliseconds. */
  millis: number;
};

/** "18 min", "1 h 25 min": drive time from the routing service. */
export const formatDriveTime = (millis: number) => {
  const minutes = Math.max(1, Math.round(millis / 60_000));
  if (minutes < 60) return `${minutes} min`;
  const rest = minutes % 60;
  return rest ? `${Math.floor(minutes / 60)} h ${rest} min` : `${minutes / 60} h`;
};

export const METERS_PER_MILE = 1609.344;
